// Repeat limits (#861): how many live packs on one surface may share a
// player, a team-level side or a market shape (the mix of markets a pack is
// built from). The server decides what a subject is (prepacks.PackSubjects);
// Studio only names and counts what it reports.

// The server's bounds (prepacks.MaxRepeatLimit); 0 is no limit.
export const MAX_REPEAT_LIMIT = 20
// What an organization gets before it saves its own (the migration's default).
export const DEFAULT_REPEAT_LIMITS = Object.freeze({ players: 2, teams: 2, markets: 4 })

/** The subject's key: `kind:key`, unique across kinds. */
export const repeatSubjectKey = (subject) => `${subject?.kind}:${subject?.key}`

const wholeLimit = (value, fallback) => {
  const number = Number(value)
  return Number.isInteger(number) && number >= 0 ? number : fallback
}

/** The configuration's limits, `{ players, teams, markets }`, the defaults for any it lacks. */
export function repeatLimitsOf(config) {
  const saved = config?.repeat_limits && typeof config.repeat_limits === 'object' ? config.repeat_limits : {}
  return {
    players: wholeLimit(saved.players, DEFAULT_REPEAT_LIMITS.players),
    teams: wholeLimit(saved.teams, DEFAULT_REPEAT_LIMITS.teams),
    markets: wholeLimit(saved.markets, DEFAULT_REPEAT_LIMITS.markets),
  }
}

const packsWord = (count) => `${count} pack${count === 1 ? '' : 's'}`

// [name, limit] pairs in words: "2 packs per player, team or market shape"
// when they agree, else each one ("3 packs per player, 2 per market shape").
// A kind at 0 is left out; null when none is limited.
function limitsInWords(pairs) {
  const kinds = pairs
    .map(([name, limit]) => [name, wholeLimit(limit, 0)])
    .filter(([, limit]) => limit > 0)
  if (kinds.length === 0) return null
  const [[firstName, firstLimit]] = kinds
  if (kinds.every(([, limit]) => limit === firstLimit)) {
    const names = kinds.map(([name]) => name)
    const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names.at(-1)}` : names[0]
    return `${packsWord(firstLimit)} per ${list}`
  }
  return [`${packsWord(firstLimit)} per ${firstName}`, ...kinds.slice(1).map(([name, limit]) => `${limit} per ${name}`)].join(', ')
}

/** The organization's limits in words, as the server's run notes say them. */
export function repeatLimitsLabel(limits) {
  return limitsInWords([['player', limits?.players], ['team', limits?.teams], ['market shape', limits?.markets]])
}

/**
 * One publish or update warning (the server's PrepackRepeatWarning) as a
 * sentence: "Colston Loveland is now in 3 packs on Home page (limit 2)".
 * `surfaceName` names a surface slug; the slug itself when it has none.
 */
export function repeatWarningText(warning, surfaceName = (slug) => slug) {
  const surface = surfaceName(warning.surface) || warning.surface
  const subject = warning.kind === 'market' ? `The ${warning.label} market shape` : warning.label
  return `${subject} is now in ${warning.packs} packs on ${surface} (limit ${warning.limit})`
}

/** Every warning of the given publish or update answers as sentences, each once. */
export function repeatWarningLines(answers, surfaceName) {
  const warnings = (Array.isArray(answers) ? answers : [answers])
    .flatMap((answer) => (Array.isArray(answer?.warnings) ? answer.warnings : []))
    .filter((warning) => warning && typeof warning === 'object' && warning.label)
  return [...new Set(warnings.map((warning) => repeatWarningText(warning, surfaceName)))]
}

/** A surface-name lookup over a surfaces list. */
export const surfaceNamer = (surfaces) => {
  const names = new Map((surfaces || []).map((surface) => [surface.slug, surface.name]))
  return (slug) => names.get(slug) || slug
}
